import { View, ViewDensity } from "@common/types/views/views";
import {
    AMOUNT_COLUMN_SIZE,
    DATE_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { ViewConfig } from "@lib/views";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    IntercompanyAccountCell,
    IntercompanyAmountCell,
    IntercompanyDateCell,
    IntercompanyDescriptionCell,
    IntercompanyEntityCell,
    IntercompanyMemoCell,
    IntercompanyMatchCell,
    IntercompanyReferenceCell,
    IntercompanySourceCell,
    IntercompanyStatusCell,
} from "./intercompanyTransactionsCells";
import { IntercompanyTransactionRow } from "./intercompanyTransactions.lib";

export const INTERCOMPANY_GL_ACCOUNT_LABEL = "General ledger";

export enum IntercompanyTransactionsColumn {
    DATE = "date",
    ENTITY = "entity",
    SOURCE = "source",
    REFERENCE = "reference",
    ACCOUNT = "account",
    DESCRIPTION = "description",
    MEMO = "memo",
    AMOUNT = "amount",
    STATUS = "status",
    MATCH = "match",
}

export interface IntercompanyTransactionsTableCallbacks {
    onCreateJournalEntry: (row: IntercompanyTransactionRow) => void;
    onFindTransactionMatch: (row: IntercompanyTransactionRow) => void;
    onOpenJournalEntry: (row: IntercompanyTransactionRow) => void;
    onOpenMatch: (row: IntercompanyTransactionRow) => void;
    onOpenTransaction: (row: IntercompanyTransactionRow) => void;
}

export interface IntercompanyTransactionsViewOptions {
    readonly?: boolean;
}

export type IntercompanyTransactionsTableMeta = ViewTableMeta<
    IntercompanyTransactionsTableCallbacks,
    IntercompanyTransactionsViewOptions
>;

export const intercompanyTransactionsViewConfig: ViewConfig<
    IntercompanyTransactionRow,
    IntercompanyTransactionsColumn,
    "date_asc" | "date_desc"
> = {
    columns: {
        [IntercompanyTransactionsColumn.DATE]: {
            accessorKey: "date",
            name: "Date",
            header: "Date",
            cell: IntercompanyDateCell,
            ...DATE_COLUMN_SIZE,
            sort: { asc: "date_asc", desc: "date_desc" },
        },
        [IntercompanyTransactionsColumn.ENTITY]: {
            accessorKey: "entityName",
            name: "Entity",
            header: "Entity",
            cell: IntercompanyEntityCell,
            minSize: 80,
            maxSize: 80,
        },
        [IntercompanyTransactionsColumn.SOURCE]: {
            accessorKey: "sourceLabel",
            name: "Source",
            header: "Source",
            cell: IntercompanySourceCell,
            minSize: 120,
            maxSize: 180,
        },
        [IntercompanyTransactionsColumn.REFERENCE]: {
            accessorKey: "id",
            name: "Reference",
            header: "Reference",
            cell: IntercompanyReferenceCell,
            minSize: 130,
            maxSize: 220,
        },
        [IntercompanyTransactionsColumn.ACCOUNT]: {
            accessorKey: "accountLabel",
            name: INTERCOMPANY_GL_ACCOUNT_LABEL,
            header: INTERCOMPANY_GL_ACCOUNT_LABEL,
            cell: IntercompanyAccountCell,
            minSize: 190,
            maxSize: 320,
        },
        [IntercompanyTransactionsColumn.DESCRIPTION]: {
            accessorKey: "description",
            name: "Description",
            header: "Description",
            cell: IntercompanyDescriptionCell,
            minSize: 200,
            maxSize: 600,
        },
        [IntercompanyTransactionsColumn.MEMO]: {
            accessorKey: "memo",
            name: "Memo",
            header: "Memo",
            cell: IntercompanyMemoCell,
            minSize: 200,
            maxSize: 400,
        },
        [IntercompanyTransactionsColumn.AMOUNT]: {
            accessorKey: "amount",
            name: "Amount",
            header: "Amount",
            cell: IntercompanyAmountCell,
            ...AMOUNT_COLUMN_SIZE,
        },
        [IntercompanyTransactionsColumn.STATUS]: {
            accessorKey: "status",
            name: "Status",
            header: "Status",
            cell: IntercompanyStatusCell,
            minSize: 100,
            maxSize: 120,
        },
        [IntercompanyTransactionsColumn.MATCH]: {
            accessorKey: "id",
            name: "Match",
            header: "Match",
            cell: IntercompanyMatchCell,
            minSize: 280,
            maxSize: 420,
        },
    },
};

export const defaultIntercompanyTransactionsView: View = {
    columns: [
        {
            id: IntercompanyTransactionsColumn.DATE,
            options: { pin: "left" },
        },
        { id: IntercompanyTransactionsColumn.ENTITY },
        { id: IntercompanyTransactionsColumn.SOURCE },
        { id: IntercompanyTransactionsColumn.REFERENCE },
        { id: IntercompanyTransactionsColumn.ACCOUNT },
        { id: IntercompanyTransactionsColumn.DESCRIPTION },
        { id: IntercompanyTransactionsColumn.MEMO },
        { id: IntercompanyTransactionsColumn.MATCH },
        {
            id: IntercompanyTransactionsColumn.STATUS,
            options: { pin: "right" },
        },
        {
            id: IntercompanyTransactionsColumn.AMOUNT,
            options: { pin: "right" },
        },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.SM,
    },
};
